<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['send']);

const { t } = useI18n();

const scenarios = computed(() => [
  {
    key: 'greeting',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.GREETING'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.GREETING'),
  },
  {
    key: 'price',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.PRICE'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.PRICE'),
  },
  {
    key: 'photo',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.PHOTO'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.PHOTO'),
  },
  {
    key: 'addon',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.ADDON'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.ADDON'),
  },
  {
    key: 'wrongPrice',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.WRONG_PRICE'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.WRONG_PRICE'),
  },
  {
    key: 'delivery',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.DELIVERY'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.DELIVERY'),
  },
  {
    key: 'confirm',
    label: t('CAPTAIN.PLAYGROUND.SCENARIOS.CONFIRM'),
    text: t('CAPTAIN.PLAYGROUND.SCENARIO_TEXT.CONFIRM'),
  },
]);

const newMessage = ref('');
const imageDataUrl = ref('');
const imagePreviewUrl = ref('');
const audioDataUrl = ref('');
const audioPreviewUrl = ref('');
const isRecording = ref(false);
const localError = ref('');
const fileInput = ref(null);

let mediaRecorder = null;
let mediaStream = null;
let audioChunks = [];
let pendingStop = null;

const canSend = computed(
  () =>
    !props.disabled &&
    Boolean(
      newMessage.value.trim() ||
      imageDataUrl.value ||
      audioDataUrl.value ||
      isRecording.value
    )
);

const revokeUrl = url => {
  if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
};

const clearImage = () => {
  revokeUrl(imagePreviewUrl.value);
  imageDataUrl.value = '';
  imagePreviewUrl.value = '';
};

const clearAudio = () => {
  revokeUrl(audioPreviewUrl.value);
  audioDataUrl.value = '';
  audioPreviewUrl.value = '';
};

const blobToDataUrl = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const compressImage = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        let { width, height } = img;
        if (width > max || height > max) {
          const scale = Math.min(max / width, max / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const attachFile = async file => {
  if (!file || !file.type.startsWith('image/')) {
    localError.value = t('CAPTAIN.PLAYGROUND.IMAGE_TYPE');
    return;
  }
  if (file.size > 8 * 1024 * 1024) {
    localError.value = t('CAPTAIN.PLAYGROUND.IMAGE_TOO_LARGE');
    return;
  }
  try {
    const dataUrl = await compressImage(file);
    if (dataUrl.length > 4_700_000) {
      localError.value = t('CAPTAIN.PLAYGROUND.IMAGE_TOO_LARGE');
      return;
    }
    clearImage();
    imageDataUrl.value = dataUrl;
    imagePreviewUrl.value = dataUrl;
    localError.value = '';
  } catch {
    localError.value = t('CAPTAIN.PLAYGROUND.IMAGE_TYPE');
  }
};

const onFileChange = event => {
  const [file] = event.target.files || [];
  attachFile(file);
  event.target.value = '';
};

const handlePaste = event => {
  const items = Array.from(event.clipboardData?.items || []);
  const imageItem = items.find(item => item.type.startsWith('image/'));
  if (!imageItem) return;
  const file = imageItem.getAsFile();
  if (!file) return;
  event.preventDefault();
  attachFile(file);
};

const stopTracks = () => {
  if (!mediaStream) return;
  mediaStream.getTracks().forEach(track => track.stop());
  mediaStream = null;
};

const finishRecording = async () => {
  const blob = new Blob(audioChunks, {
    type: mediaRecorder?.mimeType || 'audio/webm',
  });
  audioChunks = [];
  stopTracks();
  mediaRecorder = null;
  isRecording.value = false;
  if (!blob.size) {
    pendingStop?.();
    pendingStop = null;
    return;
  }
  clearAudio();
  audioPreviewUrl.value = URL.createObjectURL(blob);
  audioDataUrl.value = await blobToDataUrl(blob);
  pendingStop?.();
  pendingStop = null;
};

const startRecording = async () => {
  if (!window.MediaRecorder) {
    localError.value = t('CAPTAIN.PLAYGROUND.VOICE_UNSUPPORTED');
    return;
  }
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    audioChunks = [];
    mediaRecorder = new MediaRecorder(mediaStream, { mimeType });
    mediaRecorder.addEventListener('dataavailable', event => {
      if (event.data?.size) audioChunks.push(event.data);
    });
    mediaRecorder.addEventListener('stop', finishRecording);
    mediaRecorder.start();
    isRecording.value = true;
    localError.value = '';
  } catch {
    stopTracks();
    localError.value = t('CAPTAIN.PLAYGROUND.VOICE_UNSUPPORTED');
  }
};

const stopRecording = () =>
  new Promise(resolve => {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      resolve();
      return;
    }
    pendingStop = resolve;
    mediaRecorder.stop();
  });

const toggleRecording = () => {
  if (props.disabled) return;
  if (isRecording.value) {
    stopRecording();
    return;
  }
  startRecording();
};

const applyScenario = text => {
  newMessage.value = text;
};

const send = async () => {
  if (!canSend.value) return;
  if (isRecording.value) await stopRecording();
  emit('send', {
    text: newMessage.value.trim(),
    imageDataUrl: imageDataUrl.value,
    imagePreviewUrl: imagePreviewUrl.value,
    audioDataUrl: audioDataUrl.value,
    audioPreviewUrl: audioPreviewUrl.value,
  });
  newMessage.value = '';
  imageDataUrl.value = '';
  imagePreviewUrl.value = '';
  audioDataUrl.value = '';
  audioPreviewUrl.value = '';
  localError.value = '';
};

const openFilePicker = () => {
  fileInput.value?.click();
};

const handleEnterKey = event => {
  if (event.isComposing || event.shiftKey) return;
  event.preventDefault();
  send();
};

onUnmounted(() => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }
  stopTracks();
  clearImage();
  clearAudio();
});
</script>

<template>
  <div class="mx-6 flex flex-col gap-3">
    <div class="flex flex-wrap gap-2">
      <span class="text-xs text-n-slate-11 self-center">
        {{ t('CAPTAIN.PLAYGROUND.SCENARIOS.LABEL') }}
      </span>
      <button
        v-for="scenario in scenarios"
        :key="scenario.key"
        type="button"
        class="rounded-full px-2.5 py-1 text-xs outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        :disabled="disabled"
        @click="applyScenario(scenario.text)"
      >
        {{ scenario.label }}
      </button>
    </div>

    <div
      v-if="imagePreviewUrl || audioPreviewUrl"
      class="flex flex-wrap items-start gap-3"
    >
      <div v-if="imagePreviewUrl" class="relative">
        <img
          :src="imagePreviewUrl"
          :alt="t('CAPTAIN.PLAYGROUND.PHOTO_PREVIEW_ALT')"
          class="h-20 w-20 rounded-lg object-cover"
        />
        <NextButton
          ghost
          xs
          ruby
          icon="i-lucide-x"
          class="absolute -right-2 -top-2"
          :title="t('CAPTAIN.PLAYGROUND.REMOVE_ATTACHMENT')"
          @click="clearImage"
        />
      </div>
      <div v-if="audioPreviewUrl" class="flex items-center gap-2">
        <audio :src="audioPreviewUrl" controls class="h-8 max-w-56" />
        <NextButton
          ghost
          xs
          ruby
          icon="i-lucide-x"
          :title="t('CAPTAIN.PLAYGROUND.REMOVE_ATTACHMENT')"
          @click="clearAudio"
        />
      </div>
    </div>

    <div
      class="flex items-end gap-2 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
    >
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        class="hidden"
        @change="onFileChange"
      />
      <NextButton
        ghost
        sm
        slate
        icon="i-lucide-image"
        :disabled="disabled"
        :title="t('CAPTAIN.PLAYGROUND.ATTACH_IMAGE')"
        @click="openFilePicker"
      />
      <NextButton
        ghost
        sm
        :slate="!isRecording"
        :ruby="isRecording"
        :icon="isRecording ? 'i-lucide-square' : 'i-lucide-mic'"
        :disabled="disabled"
        :title="
          isRecording
            ? t('CAPTAIN.PLAYGROUND.STOP_RECORDING')
            : t('CAPTAIN.PLAYGROUND.RECORD_VOICE')
        "
        @click="toggleRecording"
      />
      <textarea
        v-model="newMessage"
        rows="2"
        class="flex-1 max-h-32 resize-none bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        :disabled="disabled"
        @paste="handlePaste"
        @keydown.enter.exact="handleEnterKey"
      />
      <NextButton
        ghost
        sm
        :disabled="!canSend"
        icon="i-lucide-send"
        @click="send"
      />
    </div>
    <p v-if="isRecording" class="mb-0 text-xs text-n-ruby-11">
      {{ t('CAPTAIN.PLAYGROUND.RECORDING') }}
    </p>
    <p v-if="localError" class="mb-0 text-xs text-n-ruby-11">
      {{ localError }}
    </p>
  </div>
</template>
